import { useState, useEffect, useMemo, useRef } from 'react';
import DiceLogo from './DiceLogo';
import NewUserModal from './modals/NewUserModal';
import AuthModal from './modals/AuthModal';
import PromoModal from './modals/PromoModal';
import LiveMusicModal from './modals/LiveMusicModal';
import InstallPromptModal from './modals/InstallPromptModal';
import FeedbackModal from './modals/FeedbackModal';
import LegalModal from './modals/LegalModal';
import StatsCard from './StatsCard';
import { useAuthContext } from '../contexts/AuthContext';
import { emojiPool } from '../services/themes';

export default function SetupView({ allUsers, onStartGame, onCreateUser, onJoinGame }) {
  const { user, linkedProfile, signOut } = useAuthContext();

  // Unified player list. Each entry:
  //   { id, name, avatar, score, lastTurn, userId|null, authUid|null, isRemote }
  // userId === null means a guest (no saved profile, no stat tracking).
  // isRemote === true means they'll play from their own phone (we auto-invite if they have a profile).
  const [players, setPlayers] = useState([]);
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autoAddedHostRef = useRef(false);

  const [showUserModal, setShowUserModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPromo, setShowPromo] = useState(false);
  const [showMusic, setShowMusic] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [legalTab, setLegalTab] = useState(null);
  const [starting, setStarting] = useState(false);
  const [joinCode, setJoinCode] = useState('');

  // Auto-add the signed-in user as the first/host slot, once per session.
  // The ref guard ensures this fires at most once even if linkedProfile changes
  // (e.g., sign-in completes after initial render).
  useEffect(() => {
    if (autoAddedHostRef.current) return;
    if (!linkedProfile) return;
    if (players.length > 0) return;
    autoAddedHostRef.current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPlayers([{
      id: linkedProfile.id,
      name: linkedProfile.name,
      avatar: emojiPool[0],
      score: 0,
      lastTurn: 'Waiting...',
      userId: linkedProfile.id,
      authUid: linkedProfile.authUid || null,
      isRemote: false,
    }]);
  }, [linkedProfile, players.length]);

  // Filter saved profiles by query, exclude already-added.
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allUsers
      .filter(u => !players.some(p => p.userId && p.userId === u.id))
      .filter(u => u.name.toLowerCase().includes(q))
      .slice(0, 5);
  }, [allUsers, players, query]);

  function addProfilePlayer(savedUser) {
    if (players.some(p => p.userId === savedUser.id)) {
      setQuery('');
      setShowSuggestions(false);
      return;
    }
    setPlayers(prev => [...prev, {
      id: savedUser.id,
      name: savedUser.name,
      avatar: emojiPool[prev.length % emojiPool.length],
      score: 0,
      lastTurn: 'Waiting...',
      userId: savedUser.id,
      authUid: savedUser.authUid || null,
      isRemote: false,
    }]);
    setQuery('');
    setShowSuggestions(false);
  }

  function addGuestPlayer(name) {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (players.some(p => p.name.toLowerCase() === trimmed.toLowerCase())) {
      alert('A player with that name is already added.');
      return;
    }
    setPlayers(prev => [...prev, {
      id: `guest_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name: trimmed,
      avatar: emojiPool[prev.length % emojiPool.length],
      score: 0,
      lastTurn: 'Waiting...',
      userId: null,
      authUid: null,
      isRemote: false,
    }]);
    setQuery('');
    setShowSuggestions(false);
  }

  async function addNewProfilePlayer(name) {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const newUser = await onCreateUser(trimmed);
      addProfilePlayer(newUser);
    } catch (e) {
      console.error('Could not create profile:', e);
      alert('Could not create profile. Try again, or add as Guest instead.');
    }
  }

  function removePlayer(playerId) {
    setPlayers(prev => prev.filter(p => p.id !== playerId));
  }

  async function togglePillProfile(playerId) {
    const player = players.find(p => p.id === playerId);
    if (!player) return;

    // Profile → Guest: detach but remember the original profile so we can restore on re-tap.
    if (player.userId) {
      setPlayers(prev => prev.map(p => p.id === playerId ? {
        ...p,
        previousUserId: p.userId,
        previousAuthUid: p.authUid,
        userId: null,
        authUid: null,
      } : p));
      return;
    }

    // Guest → Profile, with a remembered profile: restore.
    if (player.previousUserId) {
      setPlayers(prev => prev.map(p => p.id === playerId ? {
        ...p,
        userId: p.previousUserId,
        authUid: p.previousAuthUid || null,
        previousUserId: undefined,
        previousAuthUid: undefined,
      } : p));
      return;
    }

    // Guest → Profile, no prior profile: offer to save as a new profile.
    const ok = window.confirm(
      `Save "${player.name}" as a new profile? Stats will track for them in future games.`
    );
    if (!ok) return;
    try {
      const newUser = await onCreateUser(player.name);
      setPlayers(prev => prev.map(p => p.id === playerId ? {
        ...p,
        id: newUser.id,
        userId: newUser.id,
        authUid: newUser.authUid || null,
      } : p));
    } catch (e) {
      console.error('Could not create profile:', e);
      alert('Could not create profile. Try again.');
    }
  }

  async function togglePillRemote(playerId) {
    const player = players.find(p => p.id === playerId);
    if (!player) return;

    // If toggling an existing remote → local, just flip it.
    if (player.isRemote) {
      setPlayers(prev => prev.map(p => p.id === playerId ? { ...p, isRemote: false } : p));
      return;
    }

    // Toggling local → remote. Profile players can be invited; guests can't (no
    // identity to address an invite to). Offer to save the guest as a profile.
    if (!player.userId) {
      const ok = window.confirm(
        `To play from their own phone, "${player.name}" needs a saved profile. Save now? (Tap Cancel to share the game code with them after starting instead.)`
      );
      if (!ok) return;
      try {
        const newUser = await onCreateUser(player.name);
        setPlayers(prev => prev.map(p => p.id === playerId ? {
          ...p,
          id: newUser.id,
          userId: newUser.id,
          authUid: newUser.authUid || null,
          isRemote: true,
        } : p));
      } catch (e) {
        console.error('Could not create profile:', e);
        alert('Could not create profile. The game code still works — share it with them after you start.');
      }
      return;
    }

    setPlayers(prev => prev.map(p => p.id === playerId ? { ...p, isRemote: true } : p));
  }

  async function handleStartGame() {
    if (players.length < 1) return;
    setStarting(true);
    // Strip UI-only memory fields before persisting the game doc.
    const cleanPlayers = players.map(({ previousUserId, previousAuthUid, ...rest }) => rest); // eslint-disable-line no-unused-vars
    const gameId = await onStartGame(cleanPlayers, 'beaumonts');
    if (!gameId) setStarting(false);
  }

  const remoteCount = players.filter(p => p.isRemote).length;
  const startLabel = starting
    ? 'Creating Game...'
    : players.length < 2
      ? 'Create Game Lobby'
      : 'Start Game';

  return (
    <div id="setup-view">
      <div className="setup-container">
        <header className="header">
          <DiceLogo />
          <div className="grid-layout">
            <div className="grid-box box-left">
              <h2>dice<br/>dice<br/>baby.</h2>
            </div>
            <div className="grid-box box-right">
              <p className="tagline">there's nothing<br/>vanilla<br/>about it.</p>
            </div>
          </div>
        </header>

        {user && !user.isAnonymous ? (
          <div className="auth-indicator">
            <span className="auth-username">signed in as {linkedProfile?.name || user.displayName || user.email}</span>
            <button className="btn-link auth-signout" onClick={signOut}>Sign Out</button>
          </div>
        ) : (
          <div className="auth-indicator">
            <span className="guest-indicator">Guest Session</span>
            <button className="btn-link auth-signin" onClick={() => setShowAuthModal(true)}>Sign In</button>
          </div>
        )}

        {linkedProfile ? (
          <StatsCard userId={linkedProfile.id} playerName={linkedProfile.name} />
        ) : (
          <p className="stats-upsell">Sign in to track your stats across games.</p>
        )}

        <div className="join-game-section">
          <div className="join-game-row">
            <input
              type="text"
              className="join-code-input"
              placeholder="GAME CODE"
              maxLength={4}
              value={joinCode}
              onChange={e => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, ''))}
            />
            <button
              className="btn-join"
              disabled={joinCode.length !== 4}
              onClick={() => onJoinGame(joinCode)}
            >
              Watch Live
            </button>
          </div>
        </div>

        {/* ============ Unified Player Setup ============ */}
        <p className="setup-welcome">add your players and roll.</p>

        <div className="player-add">
          <input
            type="text"
            className="player-search-input"
            placeholder="Type a name or pick a saved player..."
            value={query}
            onChange={e => { setQuery(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            onKeyDown={e => {
              if (e.key === 'Enter' && query.trim()) {
                e.preventDefault();
                if (matches.length === 1) {
                  addProfilePlayer(matches[0]);
                } else {
                  addGuestPlayer(query);
                }
              }
            }}
          />

          {showSuggestions && (matches.length > 0 || query.trim()) && (
            <div className="player-suggestions">
              {matches.map(u => (
                <button
                  key={u.id}
                  type="button"
                  className="suggestion-row"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => addProfilePlayer(u)}
                >
                  <span className="suggestion-icon">👤</span>
                  <span className="suggestion-name">{u.name}</span>
                  <span className="suggestion-tag">Profile</span>
                </button>
              ))}
              {query.trim() && (
                <>
                  {matches.length > 0 && <div className="suggestion-divider"></div>}
                  <button
                    type="button"
                    className="suggestion-row"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => addGuestPlayer(query)}
                  >
                    <span className="suggestion-icon">🎲</span>
                    <span className="suggestion-name">Add &ldquo;{query.trim()}&rdquo; as Guest</span>
                    <span className="suggestion-hint">no stats</span>
                  </button>
                  <button
                    type="button"
                    className="suggestion-row"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => addNewProfilePlayer(query)}
                  >
                    <span className="suggestion-icon">➕</span>
                    <span className="suggestion-name">Save &ldquo;{query.trim()}&rdquo; as new Profile</span>
                    <span className="suggestion-hint">tracks stats</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="player-list-new">
          {players.map((p, i) => (
            <div key={p.id} className="player-card-new">
              <div className="player-avatar">{p.avatar}</div>
              <div className="player-info">
                <div className="player-name-row">
                  <span className="player-name">{p.name}</span>
                  {i === 0 && <span className="host-badge">host</span>}
                </div>
                <div className="player-pills">
                  <button
                    type="button"
                    className={`player-pill ${p.userId ? 'pill-profile' : 'pill-guest'}`}
                    onClick={() => togglePillProfile(p.id)}
                    title={p.userId ? 'Tap to play as Guest (skip stat tracking this game)' : (p.previousUserId ? 'Tap to restore Profile (track stats again)' : 'Tap to save as a new Profile')}
                  >
                    {p.userId ? '👤 Profile' : '🎲 Guest'}
                  </button>
                  <button
                    type="button"
                    className={`player-pill ${p.isRemote ? 'pill-remote' : 'pill-local'}`}
                    onClick={() => togglePillRemote(p.id)}
                    title={p.isRemote ? 'Tap to play at the table on this phone' : 'Tap to play from their own phone (we\'ll send an invite if they have a profile)'}
                  >
                    {p.isRemote ? '📱 Own phone' : '🪑 At table'}
                  </button>
                </div>
              </div>
              <button
                type="button"
                className="player-remove"
                onClick={() => removePlayer(p.id)}
                aria-label={`Remove ${p.name}`}
              >×</button>
            </div>
          ))}
          {players.length === 0 && (
            <p className="player-empty-hint">Search above to add a saved profile, or type a new name.</p>
          )}
        </div>

        {remoteCount > 0 && (
          <p className="setup-hint">
            <strong>{remoteCount}</strong> player{remoteCount === 1 ? '' : 's'} will play from their own phone — we&rsquo;ll send invites when you start.
          </p>
        )}

        <div className="setup-meta-links">
          <button className="meta-link" onClick={() => setShowFeedback(true)}>Send Feedback</button>
          <span className="meta-sep">·</span>
          <button className="meta-link" onClick={() => setLegalTab('privacy')}>Privacy</button>
          <span className="meta-sep">·</span>
          <button className="meta-link" onClick={() => setLegalTab('terms')}>Terms</button>
        </div>

      </div>

      <div className="setup-footer">
        <button
          className="btn-bank"
          disabled={players.length < 1 || starting}
          onClick={handleStartGame}
        >
          {startLabel}
        </button>
      </div>

      <NewUserModal
        show={showUserModal}
        onClose={() => setShowUserModal(false)}
        onCreateUser={onCreateUser}
        onShowPromo={() => setShowPromo(true)}
        onShowAuth={() => setShowAuthModal(true)}
      />
      <PromoModal show={showPromo} onClose={() => setShowPromo(false)} />
      <LiveMusicModal
        show={showMusic}
        onClose={() => setShowMusic(false)}
        artistName="The Drowsy Lads"
        artistTime="Tuesday, March 17th @ 4:00 PM"
      />
      <InstallPromptModal />
      <AuthModal show={showAuthModal} onClose={() => setShowAuthModal(false)} />
      <FeedbackModal show={showFeedback} onClose={() => setShowFeedback(false)} />
      <LegalModal
        show={legalTab !== null}
        initialTab={legalTab || 'privacy'}
        onClose={() => setLegalTab(null)}
      />
    </div>
  );
}
